import Link from '@components/ui/link'
import Image from 'next/image'
import { useTranslation } from 'react-i18next'

type ImageTemplate = 'Image Right' | 'Image Left' | 'center' | null

interface BannerProps {
  banner_id?: string | null
  page_id?: string | null
  banner_title?: string | null
  banner_description?: string | null
  image?: string | null
  image_template?: ImageTemplate
  banner_color?: string | null
  button_text?: string | null
  button_url?: string | null
  created_at?: string | null
  updated_at?: string | null
}

const BannerComponent: React.FC<{ banner: BannerProps }> = ({ banner }) => {
  const { t: translate } = useTranslation(['common'])

  if (banner.image_template?.toLowerCase() === 'center') {
    return <CenterBanner banner={banner} translate={translate} />
  }

  return <SideImageBanner banner={banner} />
}

const CenterBanner = ({
  banner,
  translate,
}: {
  banner: BannerProps
  translate: (key: string) => string
}) => (
  <div className="h-28 sm:min-h-80 overflow-hidden relative w-full aspect-[16/9]">
    {banner.image && (
      <Image
        src={banner.image}
        alt={translate('text-banner')}
        fill
        className="object-center"
        priority
      />
    )}
  </div>
)

const SideImageBanner = ({ banner }: { banner: BannerProps }) => {
  const isImageRight = banner.image_template === 'Image Right'
  const backgroundColor = banner.banner_color && `#${banner.banner_color}`
  const hasButton = !!(banner.button_text && banner.button_url)

  return (
    <div
      className={`flex flex-col lg:flex-row rounded-none sm:rounded-lg overflow-hidden shadow-md gap-0 lg:min-h-72 min-h-28 ${
        isImageRight ? 'flex-col-reverse' : 'flex-col'
      }`}
    >
      <ContentBlock
        isImageRight={isImageRight}
        backgroundColor={backgroundColor}
        title={banner.banner_title}
        description={banner.banner_description}
        hasButton={hasButton}
        buttonText={banner.button_text}
        buttonUrl={banner.button_url}
      />

      <ImageBlock isImageRight={isImageRight} imageUrl={banner.image} />
    </div>
  )
}

const ContentBlock = ({
  isImageRight,
  backgroundColor,
  title,
  description,
  hasButton,
  buttonText,
  buttonUrl,
}: {
  isImageRight: boolean
  backgroundColor: string
  title?: string | null
  description?: string | null
  hasButton: boolean
  buttonText?: string | null
  buttonUrl?: string | null
}) => (
  <div
    className={`w-full lg:w-1/2 xl:w-1/3 p-6 flex flex-col justify-center text-white ${
      isImageRight ? 'order-1' : 'order-2'
    } text-center`}
    style={{ backgroundColor }}
  >
    {title && (
      <h2 className="text-2xl md:text-3xl font-bold mb-4 leading-snug">
        {title}
      </h2>
    )}
    {description && <p className="text-sm md:text-base mb-4">{description}</p>}
    {hasButton && buttonUrl && buttonText && (
      <Link href={buttonUrl} target="_blank">
        <button className="px-4 py-2 bg-white text-black rounded-md font-semibold hover:bg-gray-100">
          {buttonText}
        </button>
      </Link>
    )}
  </div>
)

const ImageBlock = ({
  isImageRight,
  imageUrl,
}: {
  isImageRight: boolean
  imageUrl?: string | null
}) => (
  <div
    className={`w-full lg:h-auto min-h-48 bg-[length:100%_100%] bg-center bg-no-repeat ${
      isImageRight ? 'order-2' : 'order-1'
    }`}
    style={{
      backgroundImage: imageUrl ? `url(${imageUrl})` : 'none',
    }}
  >
    <div className="w-full h-full aspect-[2.5/1] md:aspect-auto" />
  </div>
)

export default BannerComponent
